import { PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT, PARA_SUPPORT_WRITE_ROLES, type ParaSupportCardRef, type ParaSupportLinkItem } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { nocOptionsQuery } from "../../components/nocs/queries";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { PageHeader } from "../../components/page/page-header";
import { LinkDrawer } from "../../components/para-support/link-drawer";
import { LinksFilters } from "../../components/para-support/links-filters";
import { LinksTable } from "../../components/para-support/links-table";
import { paraSupportListQuery, useRemoveLink } from "../../components/para-support/queries";
import {
	EMPTY_PARA_SUPPORT_DRAFT,
	type ParaSupportDraft,
	type ParaSupportSearch,
	paraSupportDraftFrom,
	paraSupportFilterCount,
	paraSupportPaging,
	paraSupportParams,
	paraSupportSearch,
	paraSupportSearchFrom,
} from "../../components/para-support/search";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { toastApiError, toastSuccess } from "../../components/ui/toast";
import { isApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Participants › Para support — which guide, pilot, ramp assistant or
 * sighted director goes with which para athlete (the old OVR's "Para Support
 * Links"; `GET /para-support-links`, offset pages with the total). Filters in
 * the URL (`?q&noc&sport&role&page&size`). Only a para games has links: on
 * the main games the page says to switch. Linking and unlinking are
 * SUPER_ADMIN's (`PARA_SUPPORT_WRITE_ROLES`, the session's ACTIVE role);
 * every other reader sees the table alone.
 */
export const Route = createFileRoute("/_app/para-support")({
	validateSearch: paraSupportSearch,
	component: ParaSupportPage,
});

function ParaSupportPage() {
	const { t } = useTranslation("paraSupport");
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const navigate = useNavigate({ from: "/para-support" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;
	const isPara = current.games?.is_para === true;
	const canWrite = (PARA_SUPPORT_WRITE_ROLES as readonly string[]).includes(session.active_role ?? "");

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null && isPara });
	const nocs = useQuery({ ...nocOptionsQuery(gamesId ?? ""), enabled: gamesId !== null && isPara });

	const go = useCallback((next: ParaSupportSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	const applied = useMemo(() => paraSupportDraftFrom(search), [search]);
	const onApply = useCallback((d: ParaSupportDraft) => go(paraSupportSearchFrom(d, { size: search.size })), [go, search.size]);
	const f = useFilterDraft<ParaSupportDraft>(applied, onApply, EMPTY_PARA_SUPPORT_DRAFT);
	const filterCount = paraSupportFilterCount(search);

	// The sidebar switched games: its sports and links are another games'.
	useOnGamesSwitch(gamesId, () => go({ size: search.size }, true));

	const params = gamesId !== null && isPara ? paraSupportParams(gamesId, search) : null;
	const list = useQuery({
		...paraSupportListQuery(params ?? { games_id: "", page: 1, page_size: PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT }),
		enabled: params !== null,
		placeholderData: keepPreviousData,
	});

	const { page, pageSize } = paraSupportPaging(search);
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	const [drawer, setDrawer] = useState<{ open: boolean; athlete: ParaSupportCardRef | null }>({ open: false, athlete: null });
	const [removing, setRemoving] = useState<ParaSupportLinkItem | null>(null);
	const remove = useRemoveLink();

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}
	if (!isPara) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.notParaTitle")} body={t("empty.notParaBody", { games: current.games.name })} />
			</div>
		);
	}
	if (list.isError && isApiError(list.error) && list.error.code === "FORBIDDEN_ROLE") {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.forbiddenTitle")} body={t("empty.forbiddenBody")} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;
	const rows = list.data?.items ?? [];

	return (
		<div className="flex flex-col">
			<PageHeader
				title={t("title")}
				subtitle={t("subtitle", { games: current.games.name })}
				{...(canWrite
					? {
							actions: (
								<Button variant="primary" onClick={() => setDrawer({ open: true, athlete: null })}>
									{t("link")}
								</Button>
							),
						}
					: {})}
			/>

			<div className="flex flex-col gap-[1rem]">
				<LinksFilters
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={filterCount}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
				/>

				<div ref={top} className="scroll-mt-[4.5rem]">
					<LinksTable
						rows={rows}
						loading={list.isPending || (list.isFetching && list.isPlaceholderData)}
						error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
						empty={
							filterCount > 0
								? {
										title: t("empty.filteredTitle"),
										description: t("empty.filteredBody"),
										action: (
											<Button variant="outline" size="sm" onClick={f.reset}>
												{t("empty.reset")}
											</Button>
										),
									}
								: {
										title: t("empty.noneTitle", { games: current.games.name }),
										description: canWrite ? t("empty.noneBody") : t("empty.noneBodyReader"),
									}
						}
						toolbar={
							list.data ? (
								<span className="text-[0.8125rem] text-muted" aria-live="polite">
									{t("table.count", { count: total, n: formatInteger(total) })}
								</span>
							) : null
						}
						pagination={
							list.data && total > 0
								? {
										page,
										pageSize,
										total,
										onPageChange: (p) => go({ ...search, page: pageToUrl(p) }),
										onPageSizeChange: (s) => go({ ...search, size: sizeToUrl(s, PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT), page: undefined }),
										pageSizeOptions: PAGE_SIZE_CHOICES,
									}
								: undefined
						}
						canWrite={canWrite}
						onAddAnother={(l) => setDrawer({ open: true, athlete: l.athlete })}
						onRemove={setRemoving}
					/>
				</div>
			</div>

			{canWrite && (
				<>
					<LinkDrawer
						open={drawer.open}
						gamesId={gamesId}
						nocs={nocs.data?.items ?? []}
						startAthlete={drawer.athlete}
						onClose={() => setDrawer({ open: false, athlete: null })}
					/>
					<ConfirmDialog
						open={removing !== null}
						title={t("remove.title")}
						body={removing ? t("remove.body", { support: removing.support.name, athlete: removing.athlete.name }) : undefined}
						confirmLabel={t("remove.confirm")}
						tone="danger"
						pending={remove.isPending}
						onCancel={() => setRemoving(null)}
						onConfirm={() => {
							if (!removing) return;
							remove.mutate(
								{ athleteCardId: removing.athlete.ac_card_id, supportCardId: removing.support.ac_card_id },
								{
									onSuccess: () => {
										toastSuccess(t("remove.done"));
										setRemoving(null);
									},
									onError: (e) => {
										toastApiError(e);
										setRemoving(null);
									},
								},
							);
						}}
					/>
				</>
			)}
		</div>
	);
}
